import React, { useState, useEffect, useId } from 'react';
import { Clock3, History, Redo2, Undo2, X } from 'lucide-react';
import { useProjectData } from '@/features/projects/ProjectDataContext';
import { useModalEscape } from '@/shared/services/KeyboardEventManager';
import { log } from '@/shared/utils/logger';
import { cn, useTheme } from '@/ui/theme/ThemeContext';
import { useSettings } from '@/features/settings/contexts/SettingsContext';

interface UndoHistoryEntry {
  name: string;
  timestamp: number;
  category: string;
  isCurrentPosition: boolean;
  canJumpTo: boolean;
  metadata?: Record<string, unknown>;
}

export const UndoHistoryPanel: React.FC = () => {
  const titleId = useId();
  const { theme } = useTheme();
  const { t } = useSettings();
  const { 
    getUndoHistoryList, 
    jumpToHistoryPosition, 
    undoState,
    undo,
    redo 
  } = useProjectData();
  
  const [historyList, setHistoryList] = useState<UndoHistoryEntry[]>([]);
  const [isVisible, setIsVisible] = useState(false);

  // Register ESC handler for this panel with high priority
  useModalEscape('undo-history-panel', () => setIsVisible(false), {
    priority: 200, // High priority for panels
    enabled: isVisible
  });

  // Destructure specific fields to avoid unstable object dependency
  const { position, stackSize } = undoState;

  // Update history list when panel is visible and undo position/size changes
  useEffect(() => {
    if (!isVisible) return;

    const updateHistoryList = () => {
      try {
        const list = getUndoHistoryList();
        setHistoryList(list);
      } catch (error) {
        log.error('UndoHistoryPanel', 'Failed to get undo history list:', error);
        setHistoryList([]);
      }
    };

    updateHistoryList();

    // Refresh every second to keep timestamps current while visible
    const interval = setInterval(updateHistoryList, 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, position, stackSize]);

  const formatTimestamp = (timestamp: number): string => {
    const now = Date.now();
    const diff = now - timestamp;
    
    if (diff < 1000) return t('just now');
    if (diff < 60000) return t('{{count}}s ago', { count: Math.floor(diff / 1000) });
    if (diff < 3600000) return t('{{count}}m ago', { count: Math.floor(diff / 60000) });
    if (diff < 86400000) return t('{{count}}h ago', { count: Math.floor(diff / 3600000) });
    return t('{{count}}d ago', { count: Math.floor(diff / 86400000) });
  };

  const getCategoryColor = (category: string): string => {
    switch (category) {
      case 'transform': return theme.infoState;
      case 'edit': return 'text-green-600 dark:text-green-400';
      case 'organize': return 'text-purple-600 dark:text-purple-400';
      case 'navigate': return 'text-orange-600 dark:text-orange-400';
      case 'compound': return 'text-red-600 dark:text-red-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  const handleJumpToPosition = async (index: number) => {
    try {
      await jumpToHistoryPosition(index);
    } catch (error) {
      log.error('UndoHistoryPanel', 'Failed to jump to history position:', error);
    }
  };

  if (!isVisible) {
    return (
      <button
        onClick={() => setIsVisible(true)}
        className={cn(
          'fixed bottom-4 right-4 z-[10010] inline-flex h-12 w-12 items-center justify-center rounded-2xl border shadow-lg transition-all duration-200 hover:shadow-xl',
          theme.bgSecondary,
          theme.borderPrimary,
          theme.textPrimary,
          theme.hover
        )}
        title={t('Show Undo History')}
        data-testid="undo-history-trigger"
      >
        <Clock3 className="h-5 w-5" />
      </button>
    );
  }

  return (
    <div 
      className="fixed inset-0 z-[10010]"
      onClick={(e) => e.target === e.currentTarget && setIsVisible(false)}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      data-testid="undo-history-sheet"
    >
      <div data-testid="undo-history-scrim" className="absolute inset-0 bg-black/45 backdrop-blur-sm" onClick={() => setIsVisible(false)} aria-hidden="true" />
      <div 
        className={cn(
          'pointer-events-auto fixed bottom-4 right-4 flex w-[min(30rem,calc(100vw-1.5rem))] max-w-full flex-col overflow-hidden rounded-2xl border shadow-2xl',
          'max-h-[min(44rem,calc(100vh-2rem))]',
          theme.bgSecondary,
          theme.borderPrimary
        )}
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header */}
        <div className={cn('flex items-start justify-between gap-3 border-b px-4 py-4', theme.borderPrimary)}>
          <div className="flex items-center gap-2">
            <div className={cn('flex h-9 w-9 items-center justify-center rounded-xl border', theme.bgPrimary, theme.borderPrimary)}>
              <History className="h-4 w-4" />
            </div>
            <div>
              <h2 id={titleId} className={cn('text-base font-semibold', theme.textPrimary)}>
                {t('Undo History')}
              </h2>
              <div className={cn('text-xs', theme.textMuted)}>
                {undoState.stackSize} {t('operations')} · {t('Position')}: {undoState.position + 1}
              </div>
            </div>
          </div>
          <button
            type="button"
            aria-label={t('Close undo history')}
            onClick={() => setIsVisible(false)}
            className={cn('rounded-lg p-2 transition-colors', theme.textMuted, theme.hover)}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Controls */}
        <div className={cn('flex items-center gap-2 border-b px-4 py-3', theme.bgPrimary, theme.borderPrimary)}>
          <button
            onClick={() => undo().catch(error => log.error('UndoHistoryPanel', 'Button undo failed:', error))}
            disabled={!undoState.canUndo}
            className={cn(
              'inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium transition-colors disabled:opacity-50',
              theme.borderPrimary,
              theme.bgSecondary,
              theme.textPrimary,
              theme.hover
            )}
          >
            <Undo2 className="h-4 w-4" />
            {t('Undo')}
          </button>
          <button
            onClick={() => redo().catch(error => log.error('UndoHistoryPanel', 'Button redo failed:', error))}
            disabled={!undoState.canRedo}
            className={cn(
              'inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium transition-colors disabled:opacity-50',
              theme.borderPrimary,
              theme.bgSecondary,
              theme.textPrimary,
              theme.hover
            )}
          >
            <Redo2 className="h-4 w-4" />
            {t('Redo')}
          </button>
          <div className={cn('ml-auto rounded-full px-2.5 py-1 text-[11px]', theme.bgSecondary, theme.borderPrimary, theme.textMuted)}>
            {t('Live history')}
          </div>
        </div>

        {/* History List */}
        <div className="flex-1 overflow-y-auto">
          {historyList.length === 0 ? (
            <div className={cn('flex items-center justify-center py-8', theme.textMuted)}>
              <div className="text-center">
                <div className="text-4xl mb-2">📝</div>
                <div>{t('No operations yet')}</div>
                <div className="text-xs mt-1">{t('Start making changes to see history')}</div>
              </div>
            </div>
          ) : (
            <div className="p-2">
              {historyList.map((entry, index) => (
                <button
                  type="button"
                  disabled={!entry.canJumpTo}
                  key={index}
                  className={cn(
                    'group relative flex w-full text-left items-center gap-3 rounded-xl px-3 py-2.5 cursor-pointer transition-all duration-150',
                    entry.isCurrentPosition
                      ? cn(theme.surfaceSelected, theme.selectionRing, theme.textPrimary)
                      : theme.hover,
                    !entry.canJumpTo && 'opacity-50 cursor-not-allowed'
                  )}
                  onClick={() => entry.canJumpTo && handleJumpToPosition(index)}
                  title={entry.canJumpTo ? `${t('Jump to')}: ${entry.name}` : t('Cannot jump to this position')}
                >
                  {/* Position indicator */}
                  <span className={cn(
                    'flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-medium',
                    entry.isCurrentPosition
                      ? theme.primaryAction
                      : cn(theme.bgPrimary, theme.textSecondary, 'border', theme.borderPrimary)
                  )}>
                    {index + 1}
                  </span>

                  {/* Category icon */}
                  <span className={cn('flex-shrink-0')}>
                    <span className={cn('block h-2.5 w-2.5 rounded-full bg-current', getCategoryColor(entry.category))} />
                  </span>

                  {/* Operation details */}
                  <span className="flex-1 min-w-0">
                    <span className={cn('text-sm font-medium truncate', theme.textPrimary)}>
                      {entry.name}
                    </span>
                    <span className={cn('text-xs flex items-center gap-2', theme.textMuted)}>
                      <span className="capitalize">{t(entry.category)}</span>
                      <span>•</span>
                      <span>{formatTimestamp(entry.timestamp)}</span>
                      {typeof entry.metadata?.affectedCount === 'number' && (
                        <>
                          <span>•</span>
                          <span>{entry.metadata.affectedCount} {t('ops')}</span>
                        </>
                      )}
                    </span>
                  </span>

                  {/* Current position indicator */}
                  {entry.isCurrentPosition && (
                    <span className={cn('flex-shrink-0', theme.textSecondary)}>
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z" clipRule="evenodd" />
                      </svg>
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={cn('p-3 border-t text-xs', theme.bgPrimary, theme.borderPrimary, theme.textMuted)}>
          <div className="flex items-center justify-between">
            <span>{t('Undo/Redo System')}</span>
            <span>{t('Press Esc to close')}</span>
          </div>
          <div className="mt-1 text-center">
            {t('Ctrl+Z: Undo | Ctrl+Shift+Z: Redo | Click to jump')}
          </div>
        </div>
      </div>
    </div>
  );
};
